import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import config from '../../config'
import { useAppDispatch } from '../../store/types'
import { removeLastLookFromWindows } from '../../store/windows/actions'
import { LastLookWindowInfo } from '../../store/windows/types'
import { copyAppStyles } from '../OpenFin/helpers'

const lastLookEvents =
  'BroadcastChannel' in window ? new BroadcastChannel('last-look-events') : null

interface Props {
  title: string
  windowInfo: LastLookWindowInfo
  children: ReactNode
}

const Popout: FC<Props> = ({ title, windowInfo, children }) => {
  const lastHeightRef = useRef<number | undefined>(undefined)
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const [externalWindow, setExternalWindow] = useState<Window | null>(null)
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null
  )

  const val = useRef<Window | null>(null)
  useEffect(() => {
    val.current = externalWindow
  }, [externalWindow])

  useEffect(() => {
    const el = ref.current
    if (!externalWindow || !el) return
    const resize = () => {
      const height = Math.ceil(el.getBoundingClientRect().height)
      const maxHeight = window.screen.availHeight
      if (height && height < maxHeight && height !== lastHeightRef.current) {
        const chromeHeight =
          externalWindow.outerHeight - externalWindow.innerHeight
        /* Leave lastHeightRef unset so a later observation retries instead of
           latching a window short by the whole title bar. */
        if (chromeHeight <= 0) return
        externalWindow.resizeTo(450, height + chromeHeight)
        externalWindow.focus()
        lastHeightRef.current = height
      }
    }
    /* A ResizeObserver only broadcasts during the rendering update of the
       document that created it, and ours is throttled whenever this tab is
       hidden or occluded, which is exactly when a last look arrives. Build it
       from the popout so it follows the popout's own lifecycle instead. */
    const PopoutResizeObserver = (
      externalWindow as Window & { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver
    if (!PopoutResizeObserver) {
      resize()
      return
    }
    const observer = new PopoutResizeObserver(resize)
    observer.observe(el)
    return () => {
      if (!externalWindow.closed) observer.disconnect()
    }
  }, [externalWindow])

  const renderWindow = useCallback(() => {
    const formattedTitle = title.split('/')[0]
    const left = localStorage.getItem(`${formattedTitle}Left`)
    const top = localStorage.getItem(`${formattedTitle}Top`)
    const extWindow = window.open(
      '',
      title.toString(),
      `resizable=yes,width=450,height=458,left=${left ?? '300'},top=${
        top ?? '200'
      }`
    )
    if (!extWindow) return

    if (extWindow.document.body.children.length > 0) {
      return
    }

    extWindow.addEventListener('beforeunload', () => {
      localStorage.setItem(
        `${formattedTitle}Left`,
        extWindow.screenX.toString()
      )
      localStorage.setItem(`${formattedTitle}Top`, extWindow.screenY.toString())
      lastLookEvents?.postMessage({ type: 'removed', windowInfo })
      dispatch(removeLastLookFromWindows(windowInfo))
    })

    const container = extWindow.document.createElement('div')
    extWindow.document.body.appendChild(container)
    setContainerElement(container)

    copyAppStyles(extWindow)

    extWindow.document.title = `${formattedTitle} ${config.env ?? ''}`

    setExternalWindow(extWindow)
  }, [dispatch, title, windowInfo])

  useEffect(() => {
    const unload = () => {
      if (val.current) {
        val.current.close()
      }
    }
    window.addEventListener('beforeunload', unload)
    renderWindow()
    return () => {
      window.removeEventListener('beforeunload', unload)
      unload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    containerElement &&
    externalWindow &&
    ReactDOM.createPortal(<div ref={ref}>{children}</div>, containerElement)
  )
}

export default Popout
